import { useCallback, useState } from "react";
import { useDropzone, type FileRejection } from "react-dropzone";
import styled from "@emotion/styled";
import UploadFileIcon from "@mui/icons-material/UploadFileOutlined";
import { Box, Button, Typography } from "@avenue-8/sidekick-ui";
import {
  downloadCsvTemplate,
  parseContactsCsv,
  type ParsedCsvRow,
} from "./csv-import-helpers";
import { useCsvContactsImportLimit } from "./useCsvContactsImportLimit";

type ImportContactsCsvUploadStepProps = {
  onParsed: (rows: ParsedCsvRow[]) => void;
};

export const ImportContactsCsvUploadStep = ({
  onParsed,
}: ImportContactsCsvUploadStepProps) => {
  const [error, setError] = useState<string | null>(null);
  const [isReading, setIsReading] = useState(false);
  const maxCsvContacts = useCsvContactsImportLimit();

  const onDrop = useCallback(
    async (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      if (rejectedFiles.length > 0) {
        setError("Only .csv files are supported.");
        return;
      }
      const [file] = acceptedFiles;
      if (!file) return;

      setError(null);
      setIsReading(true);
      try {
        const contents = await file.text();
        const result = parseContactsCsv(contents, maxCsvContacts);
        if (!result.ok) {
          setError(result.error);
          return;
        }
        onParsed(result.rows);
      } catch {
        setError("Couldn't read this file. Make sure it's a valid CSV.");
      } finally {
        setIsReading(false);
      }
    },
    [onParsed, maxCsvContacts],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "text/csv": [".csv"], "application/vnd.ms-excel": [".csv"] },
    maxFiles: 1,
    disabled: isReading,
  });

  return (
    <Container>
      <Dropzone {...getRootProps()} $isDragActive={isDragActive}>
        <input {...getInputProps()} />
        <UploadFileIcon fontSize="large" />
        <Typography variant="subtitle2">
          Drag and drop a CSV file, or click to browse
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Up to {maxCsvContacts} contacts per file
        </Typography>
      </Dropzone>
      {error && (
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      )}
      <Box display="flex" justifyContent="center">
        <Button variant="text" size="small" onClick={downloadCsvTemplate}>
          Download CSV template
        </Button>
      </Box>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Dropzone = styled.div<{ $isDragActive: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 6rem 1.5rem;
  border: 1px dashed ${(p) => p.theme.colors.border2};
  border-radius: 0.5rem;
  cursor: pointer;
  text-align: center;
  background-color: ${(p) =>
    p.$isDragActive ? p.theme.colors.background5 : "transparent"};

  &:hover {
    background-color: ${(p) => p.theme.colors.background5};
  }
`;
